import { v4 as uuidv4 } from 'uuid';
import { generateKeyBetween } from 'fractional-indexing';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import {
  QuoteDTO,
  QuoteProductDTO,
  QuoteItemDTO,
  QuoteTotals,
  QuoteSectionDTO,
  QuoteItemAPIDTO
} from '@setvi/shared/services/react-query';
import type { QuoteV2ItemMetadata } from 'Services/Query/QuotesV2/Types';
import { calculateGrossProfitPercentage } from './pricing-helpers';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the ID of a quote item (handles both id and _id fields)
 * Centralizes the common pattern of `item.id || item._id`
 */
export const getItemId = (item: QuoteItemDTO): string => item.id || item._id;

/**
 * Generic mapper for quote items that handles recursive traversal
 * Use this to apply transformations to all products in a quote structure
 *
 * @param items - Array of quote items to process
 * @param productMapper - Function to transform product items
 * @returns Transformed items array
 */
export const mapQuoteItems = (
  items: QuoteItemDTO[],
  productMapper: (product: QuoteProductDTO) => QuoteProductDTO
): QuoteItemDTO[] =>
  items.map(item => {
    if (item.type === 'product') {
      const product = item as QuoteProductDTO;
      const mappedProduct = productMapper(product);

      // Recursively process children
      if (mappedProduct.children && mappedProduct.children.length > 0) {
        return {
          ...mappedProduct,
          children: mapQuoteItems(
            mappedProduct.children,
            productMapper
          ) as QuoteProductDTO[]
        };
      }

      return mappedProduct;
    }

    if (item.type === 'section' && 'children' in item && item.children) {
      return {
        ...item,
        children: mapQuoteItems(
          item.children,
          productMapper
        ) as QuoteProductDTO[]
      };
    }

    return item;
  });

// ============================================================================
// Product Initialization
// ============================================================================

/**
 * Initialize a product with default quote-specific fields
 * Maps catalog product pricing to quote fields:
 * - product.price (catalog sell price) -> sell
 * - product.customerCost (customer-specific cost) or product.cost -> cost
 * Calculates initial GP values from sell and cost
 * Sets isPricingFetching flag for products that need pricing from API
 */
export const initializeProduct = (
  product: ProductDTO,
  parentId: string | null,
  type?: 'section' | 'product'
): QuoteProductDTO => {
  // Map catalog price fields to quote fields
  const sell = product.price || product.sell || 0;
  const cost = product.customerCost || product.cost || 0;

  // Calculate GP per unit (matching old quotes system)
  const totalGrossProfit = sell - cost;
  const totalGrossProfitPercentage = calculateGrossProfitPercentage(cost, sell);

  // Determine if we need to fetch pricing (only for non-freehand, non-subitem products)
  const isSubitem = parentId && type !== 'section';
  const needsPricingFetch = !isSubitem && !(product as any).isFreeHand;

  return {
    ...product,
    id: uuidv4(),
    _id: product._id,
    type: 'product',
    quantity:
      product?.overrideMinimumQuoteQuantity === false
        ? product?.minimumQuoteQuantity || 1
        : product?.quantity || 1,
    parentID: parentId,
    productId: product._id,
    children: [],
    note: null,
    isPricingFetching: needsPricingFetch,
    totalGrossProfit,
    totalGrossProfitPercentage,
    sell,
    cost,
    isEditable: product.isEditable,
    images: product.images,
    imageUrl: product.imageUrl,
    sku: product.sku,
    name: product.name,
    vendor: product.vendor,
    rfqItemId: (product as any).rfqItemId || undefined,
    ...((product as any).isFreeHand && { isFreeHand: true }),
    // Set default label fields for children (options/accessories)
    ...(parentId &&
      type !== 'section' && {
        labelType: 'option',
        customLabel: '',
        includeInTotalCost: false
      }),
    minimumQuoteQuantity: product?.minimumQuoteQuantity ?? 1,
    overrideMinimumQuoteQuantity: product?.overrideMinimumQuoteQuantity ?? false
  };
};

/**
 * Add products optimistically to quote
 */
export const addProductsOptimistically = (
  quote: QuoteDTO | undefined,
  products: ProductDTO[],
  parentId?: string | null
): QuoteDTO | undefined => {
  if (!quote) return quote;

  // Find parent type if parentId is provided
  let parentType: 'section' | 'product' | undefined;
  if (parentId) {
    const findParent = (items: QuoteItemDTO[]): QuoteItemDTO | null => {
      for (const item of items) {
        if (getItemId(item) === parentId) return item;
        if ('children' in item && item.children?.length) {
          const found = findParent(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    const parentItem = findParent(quote.items);
    if (parentItem) {
      parentType = parentItem.type as 'section' | 'product';
    }
  }

  const initializedProducts = products.map(p =>
    initializeProduct(p, parentId || null, parentType)
  );

  // If no parentId, add to root level
  if (!parentId) {
    return {
      ...quote,
      items: [...quote.items, ...initializedProducts]
    };
  }

  // Add to parent (section or product with children)
  const addToParent = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      const itemId = getItemId(item);

      if (itemId === parentId) {
        return {
          ...item,
          children: [...(item.children || []), ...initializedProducts]
        } as QuoteItemDTO;
      }

      if ('children' in item && item.children?.length) {
        return {
          ...item,
          children: addToParent(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: addToParent(quote.items)
  };
};

/**
 * Remove item from a flat QuoteItemDTO[] list (V2 split-cache shape).
 * If removing the last child of a parent product, clears labelType and customLabel.
 */
export const removeFromItems = (
  items: QuoteItemDTO[],
  itemId: string
): QuoteItemDTO[] =>
  items
    .filter(item => getItemId(item) !== itemId)
    .map(item => {
      if ('children' in item && item.children?.length) {
        const filteredChildren = removeFromItems(item.children, itemId);

        if (item.type === 'product' && filteredChildren.length === 0) {
          return {
            ...item,
            children: filteredChildren,
            labelType: '',
            customLabel: ''
          } as QuoteItemDTO;
        }

        return {
          ...item,
          children: filteredChildren
        } as QuoteItemDTO;
      }
      return item;
    });

/**
 * Remove section but keep its items - items are moved to root level
 * at the position where the section was
 */
export const removeSectionKeepItemsOptimistically = (
  quote: QuoteDTO | undefined,
  sectionId: string
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const newItems: QuoteItemDTO[] = [];

  for (const item of quote.items) {
    if (getItemId(item) === sectionId && item.type === 'section') {
      // Found the section - add its children to root level instead of the section
      if ('children' in item && item.children?.length) {
        // Compute positions for children being moved to root
        let lastPos =
          quote.items
            .filter(i => getItemId(i) !== sectionId)
            .map(i => (i as any).position as string | undefined)
            .filter(Boolean)
            .sort()
            .pop() || null;

        const childrenAtRoot = item.children.map(child => {
          const position = generateKeyBetween(lastPos, null);
          lastPos = position;
          return {
            ...child,
            parentId: null as string | null,
            parentID: null as string | null,
            position
          };
        });
        newItems.push(...(childrenAtRoot as unknown as QuoteItemDTO[]));
      }
      // Don't add the section itself
    } else {
      // Keep other items as-is
      newItems.push(item);
    }
  }

  return {
    ...quote,
    items: newItems
  };
};

/**
 * Move an item to a different section optimistically
 * Removes the item from its current location and adds it to the target section
 */
export const moveItemToSectionOptimistically = (
  quote: QuoteDTO | undefined,
  itemId: string,
  targetSectionId: string | null
): QuoteDTO | undefined => {
  if (!quote) return quote;

  // First, find and extract the item to move
  let itemToMove: QuoteItemDTO | null = null;

  const removeItemFromList = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items
      .filter(item => {
        if (getItemId(item) === itemId) {
          itemToMove = { ...item };
          return false; // Remove from current location
        }
        return true;
      })
      .map(item => {
        if ('children' in item && item.children?.length) {
          return {
            ...item,
            children: removeItemFromList(item.children)
          } as QuoteItemDTO;
        }
        return item;
      });

  // Remove item from its current location
  const itemsWithoutMovedItem = removeItemFromList(quote.items);

  if (!itemToMove) {
    return quote; // Item not found, return unchanged
  }

  // Compute position: append after last item in target container
  const getLastPos = (items: QuoteItemDTO[]): string | null =>
    items
      .map(i => (i as any).position as string | undefined)
      .filter(Boolean)
      .sort()
      .pop() || null;

  // If moving to root (null), compute position from root items
  if (targetSectionId === null) {
    const position = generateKeyBetween(
      getLastPos(itemsWithoutMovedItem),
      null
    );
    const movedItem = {
      ...itemToMove,
      parentID: null,
      position
    } as unknown as QuoteItemDTO;
    return {
      ...quote,
      items: [...itemsWithoutMovedItem, movedItem]
    };
  }

  // Find target section to compute position within its children
  let targetChildren: QuoteItemDTO[] = [];
  const findSection = (items: QuoteItemDTO[]) => {
    for (const item of items) {
      if (getItemId(item) === targetSectionId && item.type === 'section') {
        targetChildren = item.children || [];
        return;
      }
      if ('children' in item && item.children?.length) {
        findSection(item.children);
      }
    }
  };
  findSection(itemsWithoutMovedItem);

  const position = generateKeyBetween(getLastPos(targetChildren), null);
  const movedItem = {
    ...itemToMove,
    parentID: targetSectionId,
    position
  } as unknown as QuoteItemDTO;

  // Add the item to the target section
  const addToSection = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      if (getItemId(item) === targetSectionId && item.type === 'section') {
        return {
          ...item,
          children: [...(item.children || []), movedItem]
        } as QuoteItemDTO;
      }

      if ('children' in item && item.children?.length) {
        return {
          ...item,
          children: addToSection(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: addToSection(itemsWithoutMovedItem)
  };
};

/**
 * Move multiple items to a section optimistically in a single update
 * More efficient than calling moveItemToSectionOptimistically multiple times
 */
export const moveItemsToSectionOptimistically = (
  quote: QuoteDTO | undefined,
  itemIds: string[],
  targetSectionId: string | null
): QuoteDTO | undefined => {
  if (!quote || itemIds.length === 0) return quote;

  // Apply moves sequentially to get final state
  let result = quote;
  for (const itemId of itemIds) {
    result = moveItemToSectionOptimistically(result, itemId, targetSectionId);
  }
  return result;
};

/**
 * Update item optimistically in quote
 */
export const updateItemOptimistically = (
  quote: QuoteDTO | undefined,
  itemId: string,
  updates: {
    quantity?: number;
    sell?: number;
    cost?: number;
    note?: string;
    totalGrossProfit?: number;
    totalGrossProfitPercentage?: number;
    labelType?: string;
    customLabel?: string;
    includeInTotalCost?: boolean;
  }
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const updateInList = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      if (getItemId(item) === itemId && item.type === 'product') {
        const product = item as QuoteProductDTO;
        const updatedProduct = { ...product, ...updates };

        // Recalculate GP per unit if sell or cost changed (matching old quotes system)
        if (updates.sell !== undefined || updates.cost !== undefined) {
          const sell = updates.sell ?? product.sell;
          const cost = updates.cost ?? product.cost;

          updatedProduct.totalGrossProfit = sell - cost;
          updatedProduct.totalGrossProfitPercentage =
            calculateGrossProfitPercentage(cost, sell);
        }

        return updatedProduct;
      }

      if ('children' in item && item.children?.length) {
        return {
          ...item,
          children: updateInList(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: updateInList(quote.items)
  };
};

/**
 * Find item by ID in nested structure
 */
export const findItemById = (
  items: QuoteItemDTO[],
  itemId: string
): QuoteItemDTO | null => {
  for (const item of items) {
    if (getItemId(item) === itemId) {
      return item;
    }

    if ('children' in item && item.children?.length) {
      const found = findItemById(item.children, itemId);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Calculate quote totals recursively
 */
export const calculateQuoteTotals = (
  items: QuoteItemDTO[] | undefined
): QuoteTotals => {
  if (!items || items.length === 0) {
    return {
      totalCost: 0,
      totalSell: 0,
      totalGP: 0,
      totalGPPercentage: 0,
      itemCount: 0,
      unitCount: 0
    };
  }

  let totalCost = 0;
  let totalSell = 0;
  let itemCount = 0;
  let unitCount = 0;

  const processItems = (itemsList: QuoteItemDTO[], isChild = false) => {
    itemsList.forEach(item => {
      if (item.type === 'section') {
        // Sections contain products but don't have their own values
        if ('children' in item && item.children) {
          processItems(item.children);
        }
      } else if (item.type === 'product') {
        const product = item as QuoteProductDTO;
        const quantity = product.quantity || 1;
        const cost = product.cost || 0;
        const sell = product.sell || 0;

        // For children, only include in totals if includeInTotalCost is true
        const shouldIncludeInTotal =
          !isChild || (product as any).includeInTotalCost === true;

        if (shouldIncludeInTotal) {
          totalCost += cost * quantity;
          totalSell += sell * quantity;
          itemCount += 1;
          unitCount += quantity;
        }

        // Process child products
        if (product.children && product.children.length > 0) {
          processItems(product.children, true);
        }
      }
    });
  };

  processItems(items);

  const totalGP = totalSell - totalCost;
  const totalGPPercentage = totalSell > 0 ? (totalGP / totalSell) * 100 : 0;

  return {
    totalCost,
    totalSell,
    totalGP,
    totalGPPercentage,
    itemCount,
    unitCount
  };
};

/**
 * Flatten items for API submission (remove UI-only fields)
 * Based on Swagger: https://revops-api-development.azurewebsites.net/api-docs/#/Quote/EditQuote
 *
 * Returns items in the format expected by the Quote API
 */
export const extractItems = (
  items: QuoteItemDTO[],
  withMetadata = false
): QuoteItemAPIDTO[] =>
  items.map((item): QuoteItemAPIDTO => {
    if (item.type === 'product') {
      const product = item as QuoteProductDTO;

      // Build base product object
      const baseProduct: QuoteItemAPIDTO = {
        ...(product.id && { id: product.id }),
        type: 'product',
        productId: product.productId || product._id,
        quantity: product.quantity || 1,
        sell: product.sell ? Number(product.sell) : 0,
        cost: product.cost ? Number(product.cost) : 0,
        name: product.name || '',
        note: product.note || '',
        sku: product.sku || '',
        minimumQuoteQuantity: product?.minimumQuoteQuantity ?? 1,
        overrideMinimumQuoteQuantity:
          product?.overrideMinimumQuoteQuantity ?? false,
        children: product.children
          ? extractItems(product.children, withMetadata)
          : [],
        isEditable: product.isEditable
      };

      // Add vendor name if available (vendorName may exist on extended product types)
      const productWithVendorName = product as QuoteProductDTO & {
        vendorName?: string;
      };
      if (productWithVendorName.vendorName) {
        baseProduct.vendorName = productWithVendorName.vendorName;
      } else if (product.vendor?.name) {
        baseProduct.vendorName = product.vendor.name;
      }

      if (product?.labelType) {
        baseProduct.labelType = product?.labelType || '';
        baseProduct.customLabel = product?.customLabel || '';
        baseProduct.includeInTotalCost = product?.includeInTotalCost || null;
      }

      // Add freehand-specific fields
      if (product.isFreeHand) {
        baseProduct.isFreeHand = true;
      }

      // Add RFQ item ID if available
      if (product.rfqItemId) {
        baseProduct.rfqItemId = product.rfqItemId;
      }

      // Add metadata if requested
      if (withMetadata) {
        if (product.imageUrl) baseProduct.imageUrl = product.imageUrl;
        // Images might be ProductImage[] or string[] depending on the source
        if (product.images) {
          baseProduct.images = product.images.map((img: unknown) =>
            typeof img === 'string' ? img : (img as { url?: string })?.url || ''
          ) as any;
        }
      }

      return baseProduct;
    }

    if (item.type === 'section') {
      const section = item as QuoteSectionDTO;

      return {
        ...(section.id && { id: section.id }),
        type: 'section',
        name: section.name || '',
        note: section.note || '',
        children: extractItems(section.children || [], withMetadata)
      } as QuoteItemAPIDTO;
    }

    // Fallback for unknown types - return empty section
    return {
      type: 'section',
      name: '',
      note: '',
      children: []
    } as QuoteItemAPIDTO;
  });

/**
 * Get total count of product items (excluding child items)
 * Used for displaying "X items" in quote lists
 */
export const getTotalItems = (items: QuoteItemDTO[] = []): number =>
  items.reduce((total: number, item) => {
    if (item.type === 'section' && Array.isArray(item.children)) {
      return (
        total +
        item.children.filter((child: any) => child.type === 'product').length
      );
    }
    if (item.type === 'product' && !('parentID' in item && item.parentID)) {
      return total + 1;
    }
    return total;
  }, 0);

// ============================================================================
// V2 Items Transformation
// ============================================================================

/**
 * Transform flat v2 items metadata into nested QuoteItemDTO[] structure.
 * The v2 API returns a flat list with parentId references.
 * The existing UI code expects nested children arrays (QuoteItemDTO[]).
 */
const transformV2Product = (
  item: QuoteV2ItemMetadata,
  childrenMap: Map<string, QuoteV2ItemMetadata[]>
): QuoteProductDTO => {
  const p = item.product || ({} as QuoteV2ItemMetadata['product']);
  const children = (childrenMap.get(item._id) || []).map(child =>
    transformV2Product(child, childrenMap)
  );

  const sell = p.sell || 0;
  const cost = p.cost || 0;

  return {
    _id: item._id,
    id: item._id,
    type: 'product',
    productId: p.productId || '',
    name: p.name || '',
    sku: p.sku || '',
    quantity: p.quantity || 1,
    sell,
    cost,
    note: p.note || null,
    vendor: p.vendor || null,
    images: p.images || null,
    imageUrl: p.images?.[0]?.path || '',
    isFreeHand: p.isFreeHand || false,
    isEditable: p.isEditable || false,
    labelType: p.labelType || '',
    customLabel: p.customLabel || '',
    includeInTotalCost: p.includeInTotalCost || false,
    minimumQuoteQuantity: p.minimumQuoteQuantity ?? 1,
    overrideMinimumQuoteQuantity: p.overrideMinimumQuoteQuantity ?? false,
    children,
    parentID: item.parentId || null,
    position: item.position || null,
    totalGrossProfit: sell - cost,
    totalGrossProfitPercentage: calculateGrossProfitPercentage(cost, sell),
    description: p.description || '',
    packSize: p.packSize || '',
    unitOfMeasure: p.unitOfMeasure || '',
    manufacturerSku: p.manufacturerSku || ''
  } as unknown as QuoteProductDTO;
};

export const transformV2ItemsToNested = (
  flatItems: QuoteV2ItemMetadata[]
): QuoteItemDTO[] => {
  // Sort by position for correct ordering
  const sorted = [...flatItems].sort((a, b) =>
    (a.position || '').localeCompare(b.position || '')
  );

  // Group children by parentId
  const childrenMap = new Map<string, QuoteV2ItemMetadata[]>();
  const rootItems: QuoteV2ItemMetadata[] = [];

  sorted.forEach(item => {
    if (item.parentId) {
      const arr = childrenMap.get(item.parentId) || [];
      arr.push(item);
      childrenMap.set(item.parentId, arr);
    } else {
      rootItems.push(item);
    }
  });

  return rootItems.map(item => {
    if (item.type === 'section') {
      const children = (childrenMap.get(item._id) || []).map(child =>
        transformV2Product(child, childrenMap)
      );
      return {
        _id: item._id,
        id: item._id,
        type: 'section',
        name: item.section?.name || '',
        note: item.section?.note || null,
        children,
        position: item.position || null
      } as unknown as QuoteSectionDTO;
    }
    return transformV2Product(item, childrenMap);
  });
};

/**
 * Generates a fractional index position that sorts after all existing items.
 * Pass the current flat list of root-level items; returns a key suitable for
 * appending a new item at the end.
 */
export const generateNextPosition = (items: QuoteItemDTO[]): string => {
  const lastPosition =
    items
      .map(item => (item as any).position as string | undefined)
      .filter(Boolean)
      .sort()
      .pop() || null;

  return generateKeyBetween(lastPosition, null);
};

/**
 * Gets the last fractional position string from a list of items.
 * Returns null if no items have positions.
 */
export const getLastPosition = (items: QuoteItemDTO[]): string | null =>
  items
    .map(item => (item as any).position as string | undefined)
    .filter(Boolean)
    .sort()
    .pop() || null;

/**
 * Gets the last position within a specific section, or root if sectionId is null.
 * Searches the nested items structure to find the right container.
 */
export const getLastPositionInContainer = (
  items: QuoteItemDTO[],
  targetSectionId: string | null
): string | null => {
  if (targetSectionId === null) {
    return getLastPosition(items);
  }

  for (const item of items) {
    if (getItemId(item) === targetSectionId && item.type === 'section') {
      return getLastPosition(item.children || []);
    }
    if ('children' in item && item.children?.length) {
      const found = getLastPositionInContainer(item.children, targetSectionId);
      if (found !== null) return found;
    }
  }

  return null;
};

/**
 * Sorts a container's items by their fractional-index `position` (ascending),
 * matching the order `transformV2ItemsToNested` produces on refetch. Items
 * without a position are kept at the end in their original relative order.
 * Returns a new array; does not mutate the input.
 */
export const sortByPosition = <T extends QuoteItemDTO>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const pa = (a as any).position as string | undefined;
    const pb = (b as any).position as string | undefined;
    if (pa == null && pb == null) return 0;
    if (pa == null) return 1;
    if (pb == null) return -1;
    return pa.localeCompare(pb);
  });

/**
 * Computes the fractional-index positions needed to insert `count` new items
 * directly after `anchorItemId`, within whichever container (root or a
 * section) the anchor lives in. Returns the resolved container `sectionId`
 * (null = root) so the caller targets the correct list, plus the ordered
 * positions (strictly between the anchor and its next sibling).
 *
 * Returns null when the anchor can't be found (caller should fall back to
 * appending at the end of its intended container).
 */
export const getInsertPositionsAfter = (
  items: QuoteItemDTO[],
  anchorItemId: string,
  count: number
): { sectionId: string | null; positions: string[] } | null => {
  const positionsBetween = (
    after: string | null,
    before: string | null
  ): string[] => {
    const result: string[] = [];
    let lo = after;
    for (let i = 0; i < count; i += 1) {
      const key = generateKeyBetween(lo, before);
      result.push(key);
      lo = key;
    }
    return result;
  };

  const findInContainer = (
    container: QuoteItemDTO[],
    sectionId: string | null
  ): { sectionId: string | null; positions: string[] } | null => {
    const sorted = sortByPosition(container);
    const idx = sorted.findIndex(i => getItemId(i) === anchorItemId);
    if (idx === -1) return null;

    const after = ((sorted[idx] as any).position as string | undefined) ?? null;
    const before =
      ((sorted[idx + 1] as any)?.position as string | undefined) ?? null;

    return { sectionId, positions: positionsBetween(after, before) };
  };

  // Anchor at the root level (products or sections both carry positions).
  const rootHit = findInContainer(items, null);
  if (rootHit) return rootHit;

  // Otherwise look inside each section's children.
  for (const item of items) {
    if (item.type === 'section' && (item as any).children?.length) {
      const hit = findInContainer((item as any).children, getItemId(item));
      if (hit) return hit;
    }
  }

  return null;
};

// ============================================================================
// Shared Mutation Helpers
// ============================================================================

/**
 * Generic recursive item updater for the nested QuoteItemDTO tree.
 * Finds the item by ID and spreads the provided updates onto it.
 * Replaces per-hook helpers like updateQuantityInItems, updateNoteInItems, etc.
 */
export const updateItemInTree = (
  items: QuoteItemDTO[],
  itemId: string,
  updates: Partial<QuoteProductDTO>
): QuoteItemDTO[] =>
  items.map(item => {
    const id = item.id || (item as any)._id;
    if (id === itemId) return { ...item, ...updates } as QuoteItemDTO;
    if ('children' in item && (item as any).children?.length) {
      return {
        ...item,
        children: updateItemInTree((item as any).children, itemId, updates)
      } as QuoteItemDTO;
    }
    return item;
  });

/**
 * Generic helper to apply server response fields to an item in the nested tree.
 * Finds the item by `serverItem._id` and spreads the extracted fields onto it.
 *
 * @param fieldExtractor - pulls the fields you need from `serverItem.product`
 *
 * @example
 *   // In quantity hook:
 *   updateItemFromServerResponse(items, serverItem, p => ({
 *     quantity: p.quantity, sell: p.sell, cost: p.cost,
 *     totalGrossProfit: p.marginAmount, totalGrossProfitPercentage: p.margin
 *   }))
 */
export const updateItemFromServerResponse = (
  items: QuoteItemDTO[],
  serverItem: any,
  fieldExtractor: (product: any) => Record<string, any>
): QuoteItemDTO[] => {
  const serverId = serverItem._id;
  const extracted = fieldExtractor(serverItem.product);

  return items.map(item => {
    const id = item.id || (item as any)._id;
    if (id === serverId) {
      return { ...item, ...extracted } as QuoteItemDTO;
    }
    if ('children' in item && (item as any).children?.length) {
      return {
        ...item,
        children: updateItemFromServerResponse(
          (item as any).children,
          serverItem,
          fieldExtractor
        )
      } as QuoteItemDTO;
    }
    return item;
  });
};

/**
 * Compose a virtual QuoteDTO from the separate quote and items caches.
 * Used by hooks that need a full QuoteDTO for helpers like updateItemOptimistically.
 */
export const composeQuoteDTO = (
  quote: any,
  items: any
): QuoteDTO | undefined => {
  if (!quote) return undefined;
  return { ...quote, items: items || [] } as unknown as QuoteDTO;
};

// Re-export from constants for backwards compatibility
export { getQuoteStatusColor } from '../constants';
